import { useEffect, useState } from 'react'
import { Sparkles, X } from 'lucide-react'
import type { AchievementUnlock } from '@/api/achievements'
import { getAchievementIcon } from '@/components/achievements/achievementIcons'
import { copy } from '@/lib/copy'

interface AchievementToastProps {
  achievements: AchievementUnlock[]
  storageKey: string
}

export function AchievementToast({ achievements, storageKey }: AchievementToastProps) {
  const [visible, setVisible] = useState<AchievementUnlock[]>([])

  useEffect(() => {
    if (achievements.length === 0) return

    const shownRaw = sessionStorage.getItem(storageKey)
    const shown = new Set<string>(shownRaw ? JSON.parse(shownRaw) : [])
    const fresh = achievements.filter((item) => !shown.has(item.code))
    if (fresh.length === 0) return

    setVisible(fresh)
    fresh.forEach((item) => shown.add(item.code))
    sessionStorage.setItem(storageKey, JSON.stringify([...shown]))
  }, [achievements, storageKey])

  if (visible.length === 0) return null

  const current = visible[0]
  const Icon = getAchievementIcon(current.icon)

  const dismiss = () => {
    setVisible((items) => items.slice(1))
  }

  return (
    <div
      className="fixed inset-x-4 bottom-4 z-50 mx-auto max-w-md animate-in slide-in-from-bottom-4 fade-in"
      data-testid="achievement-toast"
    >
      <div className="relative overflow-hidden rounded-2xl border border-primary/30 bg-card p-4 shadow-lg">
        <button
          type="button"
          className="absolute right-3 top-3 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="Dismiss achievement"
          onClick={dismiss}
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-start gap-3 pr-8">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
            <Icon className="h-5 w-5" />
          </span>
          <div>
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-primary">
              <Sparkles className="h-3.5 w-3.5" />
              {copy.achievements.unlocked}
            </p>
            <p className="mt-1 text-base font-semibold text-foreground">{current.title}</p>
            <p className="mt-1 text-sm text-muted-foreground">{current.description}</p>
            {visible.length > 1 ? (
              <p className="mt-2 text-xs text-muted-foreground">
                +{visible.length - 1} more achievement{visible.length - 1 === 1 ? '' : 's'}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}
